/* eslint-disable no-magic-numbers */
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import Typography from 'carbon-react/lib/components/typography';
import Textbox from 'carbon-react/lib/components/textbox';
import Button from 'carbon-react/lib/components/button';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UISystemIcon } from '@futrli/ui-components';
import { ROUTES, CognitoUser, Logger, ONLY_DIGITS, useWindowSize } from '@futrli/shared-services';

import styles from '../../core/common.scss';
import { confirmSignIn } from '../redux-utils/actions';
import { useGetRedirectInfoFromQueryParam } from '../../auth/components/use-get-redirect-info-from-query-param';
import { AuthError, StoreState } from '../../core/types';
import { CONFIRM_SIGN_IN } from '../constants';
import { getErrorSelector } from '../../core/redux-utils/selectors';
import { SESSION_STORAGE_REDIRECT_PATH } from '../../auth/containers/auth-container';
import { trackEvent } from '../../../../predict/src/core/tracker';
import { TRACKING_EVENTS } from '../../../../predict/src/core/constants';
import { DESKTOP_LAYOUT_LIMIT } from '../../core/constants';

const VERIFICATION_CODE_LENGTH = 6;

const LAST_N_PHONE_DIGIT = 3;

interface Props {
  loggedInUser: CognitoUser | null;
}

export const MfaForm = ({ loggedInUser }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<AuthError | null>(null);
  const [storedLoggedInUser, setStoredLoggedInUser] = useState(loggedInUser);

  const navigate = useNavigate();

  const confirmSignInError = useSelector((state: StoreState) =>
    getErrorSelector(state.authReducers, CONFIRM_SIGN_IN.ACTION)
  );

  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
      document.body.classList.add(styles.bodyStyles);
    }

    return () => {
      if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
        document.body.classList.remove(styles.bodyStyles);
      }
    };
  }, [windowWidth]);

  useEffect(() => {
    setError(confirmSignInError);
  }, [confirmSignInError]);

  useEffect(() => {
    setStoredLoggedInUser(loggedInUser);
  }, [loggedInUser]);

  const dispatch = useDispatch();

  const intl = useIntl();

  const { redirectInfo, onRedirect } = useGetRedirectInfoFromQueryParam();

  const handleVerificationCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(event.target.value);
    if (error) {
      setError(null);
    }
  };

  const handleRedirect = async () => {
    const redirectPath = sessionStorage.getItem(SESSION_STORAGE_REDIRECT_PATH);

    if (redirectPath) {
      sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_PATH);
      navigate(redirectPath);
    } else if (redirectInfo) {
      return await onRedirect();
    } else {
      navigate(ROUTES.ROOT_ROUTE);
    }
  };

  const handleSubmit = async () => {
    if (storedLoggedInUser) {
      try {
        setIsLoading(true);
        await dispatch(confirmSignIn(storedLoggedInUser, navigate, verificationCode, handleRedirect));
        trackEvent(TRACKING_EVENTS.LOGGED_IN);
      } catch (e) {
        Logger.error(e as string);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const isValidVerificationCode =
    ONLY_DIGITS.test(verificationCode) && verificationCode.length === VERIFICATION_CODE_LENGTH;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && isValidVerificationCode) {
      handleSubmit();
    }
  };

  const phoneNumber = get(loggedInUser, 'challengeParam.CODE_DELIVERY_DESTINATION', '') as string;
  let phoneNumberMask = '';
  if (phoneNumber) {
    phoneNumberMask = `***${phoneNumber.substr(phoneNumber.length - LAST_N_PHONE_DIGIT)}`;
  }

  return (
    <div className={styles.newPageContainer}>
      <div className={classNames(styles.newPageContent, styles.mobileHeight)}>
        <div className={styles.registerFormContainer}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <UISystemIcon iconName="dark-by-sage-logo" width={202} height={90} />
          </div>
          <Typography variant="h1" textAlign="center" marginBottom={24}>
            <FormattedMessage id="login.enter_sms_6_digit_code" defaultMessage="Enter the SMS 6-digit code" />
          </Typography>
          <Typography fontSize="16px" mb={40}>
            <FormattedMessage
              id="login.sms_sent_number_ending_in"
              defaultMessage="An SMS with a code has been sent to mobile number ending in {phoneNumberMask}."
              values={{
                phoneNumberMask: <span style={{ fontWeight: 500 }}>{phoneNumberMask}</span>
              }}
            />
          </Typography>
          <Textbox
            placeholder={intl.formatMessage({
              id: 'common.verification_code',
              defaultMessage: 'Verification code'
            })}
            value={verificationCode}
            onChange={handleVerificationCodeChange}
            onKeyDown={handleKeyDown}
            error={
              error
                ? intl.formatMessage({
                    id: 'login.invalid_verification_code',
                    defaultMessage: 'Invalid verification code'
                  })
                : undefined
            }
            disabled={isLoading}
            mb={24}
          />
          <Button
            fullWidth
            className={styles.newSubmitButton}
            disabled={!isValidVerificationCode || isLoading}
            onClick={handleSubmit}
            buttonType="primary"
            size="large"
          >
            <FormattedMessage id="common.submit" defaultMessage="Submit" />
          </Button>
        </div>
      </div>
    </div>
  );
};
